import { useState } from 'react'
import { useNavigate } from 'react-router'

import {
  useCommitRecipe,
  useRecipeV2Summary,
  useRegenerateRecipeV2,
  useStandardRecipeV2,
  type RecipeV2Doc,
} from '@ctcells/api'
import type { RecipePayload } from '@ctcells/contract'
import { dataSourceFor, PATHS } from '@ctcells/core'
import { errorMessage, t } from '@ctcells/i18n'

import { useScreenAccess } from '../screenAccess.ts'

import { changedKeys, docWithValues } from './recipeNewStandardModel.ts'
import { RecipeNewStandardForm } from './RecipeNewStandardForm.tsx'
import { declaredSchemaVersion } from './recipeStandardModel.ts'

/**
 * 「＋ 레시피」 컨테이너(eric 2026-10-12 「바로 표준 레시피로 시작」) — `GET /recipes/standard` → (칸을 고쳤으면 `POST /recipes/regenerate`)
 * → `POST /recipes`. 🔴 받은 문서 위에 고친 칸 · 이름만 얹는다(`docWithValues`). 고친 칸이 있으면 작업 명령을 서버가 다시 만든 문서로
 * 저장한다 — 낡은 명령을 그대로 싣지 않는다. 검증은 서버 저장(422)이 한다.
 * 🔴 목(데모)에서는 보내지 않는다 — 화면이 「목 데이터에서는 보낼 수 없습니다」로 단추를 막는다.
 */
export function RecipeNewStandardContainer() {
  return dataSourceFor('recipes') === 'demo' ? <RecipeNewStandardDemo /> : <RecipeNewStandardLive />
}

function RecipeNewStandardDemo() {
  const access = useScreenAccess()
  return <RecipeNewStandardForm access={access} />
}

function RecipeNewStandardLive() {
  const access = useScreenAccess()
  const navigate = useNavigate()
  const standard = useStandardRecipeV2({})
  /* 상세와 같은 본문(시료 자리 · 작업 차례)의 재료 — 템플릿 › 레시피 판과 같은 캐시 이름(`standard`) */
  const summary = useRecipeV2Summary({ doc: standard.data, cacheId: 'standard' })
  const regenerate = useRegenerateRecipeV2()
  const commit = useCommitRecipe()
  const [regenerateError, setRegenerateError] = useState<string | undefined>(undefined)

  const save = (name: string, doc: RecipeV2Doc) =>
    commit.mutate(
      { name, payload: doc as unknown as RecipePayload, schemaVersion: declaredSchemaVersion(doc) },
      {
        onSuccess: (created) =>
          void navigate(`${PATHS.recipes}/${encodeURIComponent(created.recipe_id)}`),
      },
    )

  const createError =
    regenerateError ??
    (regenerate.error === null ? undefined : errorMessage(regenerate.error)) ??
    (commit.error === null ? undefined : errorMessage(commit.error))

  return (
    <RecipeNewStandardForm
      access={access}
      creating={regenerate.isPending || commit.isPending}
      doc={standard.data}
      loading={standard.isPending}
      summary={summary.data}
      onCreate={({ name, values }) => {
        const base = standard.data
        if (base === undefined) return
        setRegenerateError(undefined)
        const next = docWithValues(base, values, name)
        if (changedKeys(base, values).length === 0) {
          save(name, next)
          return
        }
        regenerate.mutate(
          { doc: next },
          {
            onSuccess: (result) => {
              if (result.doc === null)
                setRegenerateError(
                  t('recipeNewStandard.regenerateFailed', { reason: result.error ?? '' }),
                )
              else save(name, result.doc)
            },
          },
        )
      }}
      {...((standard.error ?? summary.error) === null
        ? {}
        : { loadError: errorMessage(standard.error ?? summary.error) })}
      {...(createError === undefined ? {} : { createError })}
    />
  )
}
